import React, { useEffect, useState } from 'react';
import './Receipts.css';
import { Pane, Table, Spinner, Text, Strong, Alert, TextInputField, Select, Dialog, Button } from 'evergreen-ui';
import { PDFDownloadLink, Page, Text as PDFText, View, Document, Image, StyleSheet } from '@react-pdf/renderer';
import QRCode from 'qrcode';
import logo from '../../assets/Chasylmlogo.png';
import watermark from '../../assets/Chasylmlogo.png';

function Receipts() {
  const [receipts, setReceipts] = useState([]);
  const [filteredReceipts, setFilteredReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterDuration, setFilterDuration] = useState('');
  const [qrCodeImage, setQrCodeImage] = useState(null);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  useEffect(() => {
    const fetchReceipts = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BASE_URL}/pesapal/receipts`);
        if (!response.ok) {
          throw new Error('Failed to fetch receipts');
        }
        const data = await response.json();
        setReceipts(data);
        setFilteredReceipts(data);
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchReceipts();
  }, []);

  useEffect(() => {
    const filtered = receipts.filter(receipt => {
      const searchTerm = searchQuery.toLowerCase();
      return (
        receipt.email.toLowerCase().includes(searchTerm) ||
        receipt.phone.toLowerCase().includes(searchTerm) ||
        receipt.firstName.toLowerCase().includes(searchTerm) ||
        receipt.lastName.toLowerCase().includes(searchTerm) ||
        receipt.destinationName.toLowerCase().includes(searchTerm)
      );
    });
    setFilteredReceipts(filtered);
  }, [searchQuery, receipts]);

  useEffect(() => {
    let filtered = [...receipts];
    if (filterDuration === 'month') {
      filtered = filtered.filter(receipt => {
        const receiptDate = new Date(receipt.date);
        const today = new Date();
        return receiptDate.getMonth() === today.getMonth() && receiptDate.getFullYear() === today.getFullYear();
      });
    } else if (filterDuration === 'week') {
      filtered = filtered.filter(receipt => {
        const receiptDate = new Date(receipt.date);
        const today = new Date();
        const oneWeekAgo = new Date(today.setDate(today.getDate() - 7));
        return receiptDate >= oneWeekAgo && receiptDate <= new Date();
      });
    }
    setFilteredReceipts(filtered);
  }, [filterDuration, receipts]);

  useEffect(() => {
    if (filteredReceipts.length > 0) {
      const currentReceipt = filteredReceipts[0];
      const qrValue = `
        Tour ID: ${currentReceipt.tour_id},
        First Name: ${currentReceipt.firstName},
        Last Name: ${currentReceipt.lastName},
        Email: ${currentReceipt.email},
        Phone: ${currentReceipt.phone},
        Destination Name: ${currentReceipt.destinationName},  
        Amount: ${currentReceipt.amount},
        Currency: ${currentReceipt.currency},
        Travel Date: ${currentReceipt.travel_date},
        Number of Adults: ${currentReceipt.adult},
        Number of Children: ${currentReceipt.child},
        Remaining Amount: ${currentReceipt.remainingAmount}
        Payment Type: ${currentReceipt.paymentType},
        Transaction Date: ${currentReceipt.created_date},
        Order Tracking ID: ${currentReceipt.order_tracking_id},
        Merchant Reference: ${currentReceipt.merchant_reference},
        Payment Method: ${currentReceipt.payment_method},
        Confirmation Code: ${currentReceipt.confirmation_code},
        Payment Status Description: ${currentReceipt.payment_status_description},
        Description: ${currentReceipt.description},
        Message: ${currentReceipt.message},
        Payment Account: ${currentReceipt.payment_account},
        Payment Status Code: ${currentReceipt.payment_status_code},
        Status: ${currentReceipt.status},
      `;
      QRCode.toDataURL(qrValue, { width: 100, margin: 2 }, (err, url) => {
        if (!err) {
          setQrCodeImage(url);
        }
      });
    }
  }, [filteredReceipts]);

  const styles = StyleSheet.create({
    page: {
      padding: 30,
      position: 'relative',
      backgroundColor: '#F2EED7',
    },
    header: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      marginBottom: 20,
    },
    contactInfo: {
      top: 20,
      fontSize: 10,
      textAlign: 'right',
    },
    detailsText: {
      fontSize: 12,
    },
    logo: {
      width: 100,
      height: 80,
      marginBottom: 10,
    },
    watermark: {
      position: 'absolute',
      width: '90%',
      height: '60%',
      opacity: 0.1,
      top: 100,
      left: 0,
    },
    table: {
      display: 'table',
      width: '100%',
      borderStyle: 'solid',
      borderWidth: 1,
      margin: '10px 0',
    },
    tableRow: {
      margin: 'auto',
      flexDirection: 'row',
    },
    tableCol: {
      width: '16.66%',
      borderStyle: 'solid',
      borderWidth: 1,
      padding: 8,
    },
    tableCell: {
      margin: 'auto',
      fontSize: 10,
    },
    title: {
      fontSize: 16,
      textAlign: 'center',
      marginBottom: 10,
    },
    section: {
      marginBottom: 10,
    },
    qrCodeContainer: {
      textAlign: 'start',
      marginTop: 20,
      marginBottom: 20,
    },
    qrCode: {
      width: 100,
      height: 100,
      marginTop: 10,
    },
  });

  const ReceiptPDF = ({ receipt }) => (
    <Document>
      <Page style={styles.page}>
        <View style={styles.header}>
          <Image src={logo} style={styles.logo} />
          <View style={styles.contactInfo}>
            <PDFText>info@chasylmtours.com</PDFText>
            <PDFText>Tel: +254720727776</PDFText>
            <PDFText>Nairobi, Kenya</PDFText>
          </View>
        </View>
        <Image src={watermark} style={styles.watermark} />
        <Text style={styles.title}>Chasylm Tours & Safaris Receipt</Text>
        <View style={styles.section}>
          <PDFText style={styles.detailsText}>Name: {receipt.firstName} {receipt.lastName}</PDFText>
          <PDFText style={styles.detailsText}>Phone: {receipt.phone}</PDFText>
          <PDFText style={styles.detailsText}>Email: {receipt.email}</PDFText>
          <PDFText style={styles.detailsText}>Destination: {receipt.destinationName}</PDFText>
          <PDFText style={styles.detailsText}>Travel Date: {receipt.travel_date}</PDFText>
        </View>
        <View style={styles.table}>
          <View style={styles.tableRow}>
            <View style={styles.tableCol}><PDFText style={styles.tableCell}>Payment Method</PDFText></View>
            <View style={styles.tableCol}><PDFText style={styles.tableCell}>Amount (USD)</PDFText></View>
            <View style={styles.tableCol}><PDFText style={styles.tableCell}>Merchant Reference</PDFText></View>
            <View style={styles.tableCol}><PDFText style={styles.tableCell}>Payment Date</PDFText></View>
            <View style={styles.tableCol}><PDFText style={styles.tableCell}>No. of Adult(s)</PDFText></View>
            <View style={styles.tableCol}><PDFText style={styles.tableCell}>No. of Child(ren)</PDFText></View>
          </View>
          <View style={styles.tableRow}>
            <View style={styles.tableCol}><PDFText style={styles.tableCell}>{receipt.payment_method}</PDFText></View>
            <View style={styles.tableCol}><PDFText style={styles.tableCell}>{receipt.amount}</PDFText></View>
            <View style={styles.tableCol}><PDFText style={styles.tableCell}>{receipt.merchant_reference}</PDFText></View>
            <View style={styles.tableCol}><PDFText style={styles.tableCell}>{receipt.created_date}</PDFText></View>
            <View style={styles.tableCol}><PDFText style={styles.tableCell}>{receipt.adult}</PDFText></View>
            <View style={styles.tableCol}><PDFText style={styles.tableCell}>{receipt.child}</PDFText></View>
          </View>
        </View>
        <View style={styles.section}>
          <PDFText style={styles.detailsText}>Tracking ID: {receipt.order_tracking_id}</PDFText>
          <PDFText style={styles.detailsText}>Payment Account: {receipt.payment_account}</PDFText>
          <PDFText style={styles.detailsText}>Confirmation Code: {receipt.confirmation_code}</PDFText>
          <PDFText style={styles.detailsText}>Balance: {receipt.remainingAmount}</PDFText>
        </View>
        <View style={styles.section}>
          <PDFText style={styles.detailsText}>Message: {receipt.message}</PDFText>
        </View>
        {qrCodeImage && (
          <View style={styles.qrCodeContainer}>
            <PDFText style={styles.detailsText}>Scan for more details</PDFText>
            <Image src={qrCodeImage} style={styles.qrCode} />
          </View>
        )}
        <PDFText style={styles.detailsText}>Thank you for booking with us!</PDFText>
      </Page>
    </Document>
  );

  const handleOpenDialog = (receipt) => {
    setSelectedReceipt(receipt);
    setIsDialogOpen(true);
  };

  if (loading) {
    return (
      <Pane display="flex" alignItems="center" justifyContent="center" height={400}>
        <Spinner />
        <Text marginLeft={16}>Loading receipts...</Text>
      </Pane>
    );
  }

  if (error) {
    return (
      <Pane>
        <Alert intent="danger" title="Error loading receipts">
          {error}
        </Alert>
      </Pane>
    );
  }

  return (
    <Pane padding={16} className="receipts-container" style={{ marginTop: '10rem' }}>
      <Text size={600} marginBottom={16} display="block">
        <Strong>Receipts</Strong>
      </Text>

      <TextInputField
        label="Search Receipts"
        placeholder="Search by email, phone, first name, last name"
        value={searchQuery}
        onChange={e => setSearchQuery(e.target.value)}
      />

      <Select
        onChange={e => setFilterDuration(e.target.value)}
        placeholder="Filter by duration"
        marginBottom={16}
      >
        <option value="">All</option>
        <option value="week">This Week</option>
        <option value="month">This Month</option>
      </Select>

      {filteredReceipts.length === 0 ? (
        <Text>No receipts available</Text>
      ) : (
        <>
          <Table>
            <Table.Head>
              <Table.TextHeaderCell>ID</Table.TextHeaderCell>
              <Table.TextHeaderCell>Amount</Table.TextHeaderCell>
              <Table.TextHeaderCell>Status</Table.TextHeaderCell>
              <Table.TextHeaderCell>Travel Date</Table.TextHeaderCell>
              <Table.TextHeaderCell>Email</Table.TextHeaderCell>
              <Table.TextHeaderCell>Destination</Table.TextHeaderCell>
              <Table.TextHeaderCell>Print</Table.TextHeaderCell>
              <Table.TextHeaderCell>More Info</Table.TextHeaderCell>
            </Table.Head>
            <Table.Body>
              {filteredReceipts.map((receipt) => (
                <Table.Row key={receipt.id}>
                  <Table.TextCell>{receipt.id}</Table.TextCell>
                  <Table.TextCell>{receipt.amount}</Table.TextCell>
                  <Table.TextCell>{receipt.status}</Table.TextCell>
                  <Table.TextCell>{new Date(receipt.travel_date).toLocaleDateString()}</Table.TextCell>
                  <Table.TextCell>{receipt.email}</Table.TextCell>
                  <Table.TextCell>{receipt.destinationName}</Table.TextCell>
                  <Table.TextCell>
                    <PDFDownloadLink
                      document={<ReceiptPDF receipt={receipt} />}
                      fileName={`receipt-${receipt.id}.pdf`}
                    >
                      {({ loading }) => (loading ? 'Loading document...' : 'Print')}
                    </PDFDownloadLink>
                  </Table.TextCell>
                  <Table.TextCell>
                    <Button onClick={() => handleOpenDialog(receipt)}>More Info</Button>
                  </Table.TextCell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>

          <Dialog
            isShown={isDialogOpen}
            title="Receipt Details"
            onCloseComplete={() => setIsDialogOpen(false)}
            confirmLabel="Close"
            hasCancel={false}
          >
            {selectedReceipt && (
              <Pane>
                <Text display="block" marginBottom={8}><Strong>First Name:</Strong> {selectedReceipt.firstName}</Text>
                <Text display="block" marginBottom={8}><Strong>Last Name:</Strong> {selectedReceipt.lastName}</Text>
                <Text display="block" marginBottom={8}><Strong>Phone:</Strong> {selectedReceipt.phone}</Text>
                <Text display="block" marginBottom={8}><Strong>Currency:</Strong> {selectedReceipt.currency}</Text>
                <Text display="block" marginBottom={8}><Strong>Merchant Reference:</Strong> {selectedReceipt.merchant_reference}</Text>
                <Text display="block" marginBottom={8}><Strong>Payment Method:</Strong> {selectedReceipt.payment_method}</Text>
                <Text display="block" marginBottom={8}><Strong>Confirmation Code:</Strong> {selectedReceipt.confirmation_code}</Text>
                <Text display="block" marginBottom={8}><Strong>Payment Status Description:</Strong> {selectedReceipt.payment_status_description}</Text>
                <Text display="block" marginBottom={8}><Strong>Description:</Strong> {selectedReceipt.description}</Text>
                <Text display="block" marginBottom={8}><Strong>Message:</Strong> {selectedReceipt.message}</Text>
                <Text display="block" marginBottom={8}><Strong>Payment Account:</Strong> {selectedReceipt.payment_account}</Text>
                <Text display="block" marginBottom={8}><Strong>Payment Status Code:</Strong> {selectedReceipt.payment_status_code}</Text>
                <Text display="block" marginBottom={8}><Strong>Adults:</Strong> {selectedReceipt.adult}</Text>
                <Text display="block" marginBottom={8}><Strong>Children:</Strong> {selectedReceipt.child}</Text>
                <Text display="block" marginBottom={8}><Strong>Payment Type:</Strong> {selectedReceipt.paymentType}</Text>
                <Text display="block" marginBottom={8}><Strong>Remaining Amount:</Strong> {selectedReceipt.remainingAmount}</Text>
                <Text display="block" marginBottom={8}><Strong>Order Tracking ID:</Strong> {selectedReceipt.order_tracking_id}</Text>
                <Text display="block" marginBottom={8}><Strong>Tour ID:</Strong> {selectedReceipt.tour_id}</Text>
              </Pane>
            )}
          </Dialog>
        </>
      )}
    </Pane>
  );
}

export default Receipts;